import { subscribe, useSnapshot } from 'valtio';
import { subscribeKey } from 'valtio/utils';

import { isFeatureFlagEnabled } from '@canvas/client-feature-flags';
import { isEmbeddedSmartLink } from '@canvas/frame-api/host-api/host-frame-context';
import { Action } from '@canvas/model/permissions';
import { appPermissions } from '@canvas/permissions/singleton';
import { cannotWrite, cannotRead } from '@canvas/policies/transient';

import { can } from '../abilities';
import { Ability } from '../abilities-types';
import { useAbility } from '../use-ability';
import { useDocumentId } from '../use-document-id';

import { type InvokeParams, invokeVersioned, useVersionedHook } from './invoke-versioned';
import {
  currentPermissions,
  getEffectiveBoardAccess,
  resetPermissionState,
} from './permissions-state';

export type GrantedPermissions = Record<'containerAccess' | 'boardAccess', Set<Action>>;

export type PermissionsState = {
  /**
   * Board id the current permissions relate to
   */
  id: string | undefined;
  /**
   * Access that we have on the board level.
   */
  boardAccess: Set<Action>;
  /**
   * Access that is disabled on the board level, temporarily.
   *
   * For example, this may contain `write` to disable write access if the relay cannot
   * accept any more connections.
   */
  boardAccessDisableOverride: Set<Action>;
};

/**
 * During the transition phase from v1 -> v2, read only results will ALWAYS
 * differ for embedded smart links. This is because they are not modelled as
 * read-only boards today at a permissions level.
 *
 * Hence we check explicitly for when there is a mismatch in embedded mode, and
 * allow for it to take place.
 */
const compareReadOnlyResults = (v1: boolean, v2: boolean): boolean => {
  return v1 === v2 || (v1 === false && isEmbeddedSmartLink() && v2 === true);
};

// --- boardIsNotEmbedded ---
export const boardIsNotEmbedded = () => {
  return !isEmbeddedSmartLink();
};

// --- updatePermissionState ---
export const updatePermissionState = (
  id: string | undefined,
  opts: { board: Set<Action> }
) => {
  // dual write
  updatePermissionStateV1(id, opts);
  updatePermissionStateV2(id, opts);
};
const updatePermissionStateV1 = (
  id: string | undefined,
  opts: { board: Set<Action> }
) => {
  resetPermissionState();

  opts.board.forEach((value) => currentPermissions.boardAccess.add(value));
  currentPermissions.id = id;
};
const updatePermissionStateV2 = (
  id: string | undefined,
  opts: { board: Set<Action> }
) => {
  appPermissions.update(id, opts.board);
};

// --- updateBoardAccessOverride ---
export const updateBoardAccessOverride = (action: Action) => {
  // dual write
  updateBoardAccessOverrideV1(action);
  updateBoardAccessOverrideV2(action);
};
const updateBoardAccessOverrideV1 = (action: Action) => {
  currentPermissions.boardAccessDisableOverride.add(action);
};
const updateBoardAccessOverrideV2 = (action: Action) => {
  if (action === Action.write) {
    appPermissions.policies.add('access-override', [cannotWrite]);
  } else {
    appPermissions.policies.add('access-override', [cannotRead]);
  }
};

// --- clearBoardAccessOverride ---
export const clearBoardAccessOverride = () => {
  // dual write
  clearBoardAccessOverrideV1();
  clearBoardAccessOverrideV2();
};
const clearBoardAccessOverrideV1 = () => {
  currentPermissions.boardAccessDisableOverride.clear();
};
const clearBoardAccessOverrideV2 = () => {
  appPermissions.policies.remove('access-override');
};

// --- onPermissionsUpdated ---
export const onPermissionsUpdated = (cb: VoidFunction): VoidFunction => {
  const unsubscribeV1 = onPermissionsUpdatedV1(cb);
  const unsubscribeV2 = onPermissionsUpdatedV2(cb);

  return () => {
    unsubscribeV1();
    unsubscribeV2();
  };
};
const onPermissionsUpdatedV1 = (cb: VoidFunction) => {
  return subscribe(currentPermissions, () => {
    // a subscription is a read - only do this when reads FF is off
    if (isFeatureFlagEnabled('v2PermissionsReads')) {
      return;
    }

    cb();
  });
};
const onPermissionsUpdatedV2 = (cb: VoidFunction) => {
  return appPermissions.subscribe(() => {
    // a subscription is a read - only do this when reads FF is on
    if (!isFeatureFlagEnabled('v2PermissionsReads')) {
      return;
    }

    cb();
  });
};

// --- onBoardPermissionsUpdated ---
export const onBoardPermissionsUpdated = (cb: VoidFunction) => {
  const unsubscribeV1 = onBoardPermissionsUpdatedV1(cb);
  const unsubscribeV2 = onBoardPermissionsUpdatedV2(cb);

  return () => {
    unsubscribeV1();
    unsubscribeV2();
  };
};
const onBoardPermissionsUpdatedV1 = (cb: VoidFunction) => {
  return subscribeKey(currentPermissions, 'boardAccess', () => {
    // a subscription is a read - only do this when reads FF is off
    if (isFeatureFlagEnabled('v2PermissionsReads')) {
      return;
    }

    cb();
  });
};
const onBoardPermissionsUpdatedV2 = (cb: VoidFunction) => {
  return appPermissions.subscribe(() => {
    // a subscription is a read - only do this when reads FF is on
    if (!isFeatureFlagEnabled('v2PermissionsReads')) {
      return;
    }

    cb();
  });
};

const effectiveBoardAccessFlyweight = new Set<Action>();

// --- isBoardReadOnly ---
const isBoardReadOnlyV1 = (
  boardAccess = currentPermissions.boardAccess,
  boardAccessDisableOverride = currentPermissions.boardAccessDisableOverride
) => {
  const effectiveBoardAccess = getEffectiveBoardAccess(
    boardAccess,
    boardAccessDisableOverride,
    effectiveBoardAccessFlyweight
  );

  if (!effectiveBoardAccess.has(Action.read)) {
    return false;
  }

  return effectiveBoardAccess.has(Action.read) && !effectiveBoardAccess.has(Action.write);
};
const isBoardReadOnlyV2 = (
  _boardAccess = currentPermissions.boardAccess,
  _boardAccessDisableOverride = currentPermissions.boardAccessDisableOverride
) => {
  return can(Ability.readOnly);
};

const isBoardReadOnlyConfig: InvokeParams<typeof isBoardReadOnly> = {
  tag: 'is-board-read-only',
  invokeV1: isBoardReadOnlyV1,
  invokeV2: isBoardReadOnlyV2,
  compareResults: true,
  compareMethod: compareReadOnlyResults,
};
export const isBoardReadOnly = (
  boardAccess = currentPermissions.boardAccess,
  boardAccessDisableOverride = currentPermissions.boardAccessDisableOverride
) => {
  isBoardReadOnlyConfig.args = [boardAccess, boardAccessDisableOverride];

  return invokeVersioned(isBoardReadOnlyConfig);
};

/**
 * @privateRemarks {@link canUserEditBoard} and this are logically the same
 * except this takes the state as parameters for the sake of implementing
 * {@link useCanUserEditBoard}
 * @privateRemarks This method was created in order to avoid adding extra
 * parameters to {@link canUserEditBoard} which would break many existing use
 * cases where arbitrary values are currently being passed into the
 * {@link canUserEditBoard} (the state machine does this a lot)
 */
const extractCanUserEditBoard = (currentPermissionsDi: typeof currentPermissions) =>
  !isEmbeddedSmartLink() &&
  !isBoardReadOnlyV1(
    currentPermissionsDi.boardAccess,
    currentPermissionsDi.boardAccessDisableOverride
  );

// --- canUserEditBoard ---
/**
 * User is allowed to edit the board
 */
const canUserEditBoardV1 = () => {
  return extractCanUserEditBoard(currentPermissions);
};
const canUserEditBoardV2 = () => {
  return can(Ability.write);
};

const canUserEditBoardConfig: InvokeParams<typeof canUserEditBoardV1> = {
  tag: 'can-user-edit-board',
  invokeV1: canUserEditBoardV1,
  invokeV2: canUserEditBoardV2,
  compareResults: true,
};
export const canUserEditBoard = () => {
  return invokeVersioned(canUserEditBoardConfig);
};

// --- useCanUserEditBoard ---
export const useCanUserEditBoard = () => {
  return useVersionedHook({
    tag: 'user-can-user-edit-board',
    invokeV1: useCanUserEditBoardV1,
    invokeV2: useCanUserEditBoardV2,
    compareResults: true,
  });
};
const useCanUserEditBoardV1 = () => {
  const permissions = useSnapshot(currentPermissions);

  return extractCanUserEditBoard(permissions);
};
const useCanUserEditBoardV2 = () => {
  return useAbility(Ability.write);
};

// --- useCanUserOnlyReadBoard ---
export const useCanUserOnlyReadBoard = () => {
  return useVersionedHook({
    tag: 'user-can-only-read-board',
    invokeV1: useCanUserOnlyReadBoardV1,
    invokeV2: useCanUserOnlyReadBoardV2,
    compareMethod: compareReadOnlyResults,
    compareResults: true,
  });
};
const useCanUserOnlyReadBoardV1 = () => {
  const currentBoardAccess = useSnapshot(currentPermissions.boardAccess);
  const currentBoardAccessDisableOverride = useSnapshot(
    currentPermissions.boardAccessDisableOverride
  );

  return isBoardReadOnlyV1(currentBoardAccess, currentBoardAccessDisableOverride);
};
const useCanUserOnlyReadBoardV2 = () => {
  return useAbility(Ability.readOnly);
};

// --- canUserAddComment ---
export const canUserAddComment = () => {
  return invokeVersioned({
    tag: 'can-user-add-comment',
    invokeV1: canUserAddCommentV1,
    invokeV2: canUserAddCommentV2,
    compareResults: true,
  });
};
const canUserAddCommentV1 = () => {
  return currentPermissions.boardAccess.has(Action['add/comment']);
};
const canUserAddCommentV2 = () => {
  return can(Ability.addComment);
};

// --- useCanUserAddComment ---
export const useCanUserAddComment = () => {
  return useVersionedHook({
    tag: 'use-can-user-add-comment',
    invokeV1: useCanUserAddCommentV1,
    invokeV2: useCanUserAddCommentV2,
    compareResults: true,
  });
};
const useCanUserAddCommentV1 = () => {
  return useSnapshot(currentPermissions).boardAccess.has(Action['add/comment']);
};
const useCanUserAddCommentV2 = () => {
  return useAbility(Ability.addComment);
};

// --- canUserDeleteComment ---
/**
 * Refers to the permission that lets user remove any comment on the board,
 * regardless of whether it is their comment
 */
// --- canUserDeleteComment ---
export const canUserDeleteComment = () => {
  return invokeVersioned({
    tag: 'can-user-delete-comment',
    invokeV1: canUserDeleteCommentV1,
    invokeV2: canUserDeleteCommentV2,
    compareResults: true,
  });
};
const canUserDeleteCommentV1 = () => {
  return currentPermissions.boardAccess.has(Action['delete/comment']);
};
const canUserDeleteCommentV2 = () => {
  return can(Ability.deleteComment);
};

// --- getClaims ---
export const getClaims = (): Set<Action> => {
  return invokeVersioned({
    tag: 'get-claims',
    invokeV1: getClaimsV1,
    invokeV2: getClaimsV2,
    compareResults: true,
    compareMethod: (a, b) => a.size === b.size && Array.from(a).every((aa) => b.has(aa)),
  });
};
const getClaimsV1 = () => {
  return currentPermissions.boardAccess;
};
const getClaimsV2 = () => {
  return appPermissions.claims;
};

// --- canUserRead ---
export const canUserRead = () => {
  return invokeVersioned({
    tag: 'can-user-read',
    invokeV1: canUserReadV1,
    invokeV2: canUserReadV2,
    compareResults: true,
  });
};
const canUserReadV1 = () => {
  return getEffectiveBoardAccess().has(Action.read);
};
const canUserReadV2 = () => {
  return can(Ability.read);
};

// --- getBoardId ---
export const getBoardId = () => {
  return invokeVersioned({
    tag: 'get-board-id',
    invokeV1: getBoardIdV1,
    invokeV2: getBoardIdV2,
    compareResults: true,
  });
};
const getBoardIdV1 = () => {
  return currentPermissions.id;
};
const getBoardIdV2 = () => {
  return appPermissions.id;
};

// --- useBoardId ---
export const useBoardId = () => {
  return useVersionedHook({
    tag: 'use-board-id',
    invokeV1: useBoardIdV1,
    invokeV2: useBoardIdV2,
    compareResults: true,
  });
};
const useBoardIdV1 = () => {
  return useSnapshot(currentPermissions).id;
};
const useBoardIdV2 = () => {
  return useDocumentId();
};
